import {
  Component,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  KIND_NAME,
  STYLE,
  type MapCircle,
  type MapPoint,
  type MapPolygon,
  type MapProjectorRef,
  type MapRegion,
} from './map-style.ts';
// The floor is imported STATICALLY, not lazily: it is the fallback the nested
// error boundary renders when both engines have failed, and a lazy chunk that
// 404s (a mid-incident deploy, a degraded connection) would throw inside the
// boundary's OWN fallback — which no boundary can catch — and blank the page.
// The component whose job is to be un-blankable must not depend on a chunk load.
// It is 33 lines of static JSX; the round-trip it saves is at the worst moment.
import { Map as UnavailableImpl, MapRetryProvider } from './map-unavailable.tsx';
import { reportChunkError } from './lib/stale-build.ts';

export type {
  MapCircle,
  MapPoint,
  MapPolygon,
  MapProjector,
  MapProjectorRef,
  MapRegion,
} from './map-style.ts';

/**
 * Which tiles to draw, when the caller has not insisted.
 *
 * A bright cream street map inside a dark interface is the single loudest thing
 * on the screen, and it was exactly that on the Sites page in staging: the live
 * view passed `dark` explicitly, every other caller passed nothing, and nothing
 * failed — the map simply came back the wrong colour. The prop defaulted to
 * `false`, which made "I did not think about it" and "light, please" the same
 * statement.
 *
 * So the default is now *resolve it*, and `dark` is an override the live view
 * uses because it is dark regardless of preference. Correct without being
 * asked, still overridable when a surface genuinely knows better.
 *
 * Both signals have to be watched. The theme toggle stamps `data-theme` on the
 * root element, and `system` removes it so the media query takes over — so an
 * attribute observer alone misses somebody changing their OS theme, and a media
 * listener alone misses the toggle.
 */
function useResolvedDark(): boolean {
  const read = () => {
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'dark') return true;
    if (attr === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const [dark, setDark] = useState(read);

  useEffect(() => {
    const update = () => setDark(read());

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', update);

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => {
      media.removeEventListener('change', update);
      observer.disconnect();
    };
  }, []);

  return dark;
}

/**
 * The map engine is loaded as a separate chunk, because it is not what somebody
 * opens the live view for.
 *
 * The ring and the four numbers are the answer to "are they out". The map is
 * the answer to "where", which is the second question. Blocking the first on a
 * map-rendering library — over whatever signal exists in a stairwell — gets
 * that exactly backwards, so the map arrives when it arrives.
 *
 * The Suspense fallback reserves the full height rather than collapsing,
 * because a panel that grows when it loads shifts everything under it, and the
 * thing under it on the live view is the stand-down button.
 */
/**
 * Engine choice (the frozen design: Apple's cartography ideally — MapKit JS,
 * the engine under iCloud Find Devices — with Google Maps as the fallback, and
 * an honest "unavailable" panel as the floor). Decided once per session:
 *
 *   MapKit  — `mapkitReady()`: provider=live AND token mintable AND script
 *             loadable AND init clean.
 *   Google  — else `googleMapsReady()`: key configured AND script loadable.
 *   none    — else the map-unavailable panel.
 *
 * Every failure path degrades to the next engine that works, and the floor
 * never shows the old Carto watermark and never a grey box — a broken map on a
 * live screen is an outage, not a fallback story. The two loaders are memoised,
 * so this probe runs once and every Map on every screen shares the result.
 */
/**
 * The engines below MapKit: Google if its key is configured and its script
 * loads, else the honest unavailable panel. Shared by the chooser (MapKit not
 * available) and the error boundary (MapKit crashed after mount), so a MapKit
 * failure — before or after mount — still lands on a working Google map.
 */
async function loadFallbackEngine(): Promise<{ default: typeof import('./map-google.tsx').Map }> {
  try {
    const { googleMapsReady } = await import('./google-loader.ts');
    if (await googleMapsReady()) {
      // Reaching here means MapKit was NOT available and we are now on the
      // per-load-billed Google engine. In steady state this should be ~never
      // (MapKit is primary), so a sustained stream of these warnings is itself
      // the signal that MapKit is silently down — and the cost alarm. The
      // server logs the paired signal on /me/maps-key (routes/mapkit.ts).
      console.warn('map: MapKit unavailable — using the Google Maps fallback (billed per load)');
      const m = await import('./map-google.tsx');
      return { default: m.Map };
    }
  } catch (err) {
    // Loader or engine import failed — the unavailable panel is the answer. If
    // it failed because this tab outlived a deploy, "Try again" can never fix
    // it (a failed import is cached); the stale-build guard reloads instead.
    reportChunkError(err);
  }
  return { default: UnavailableImpl };
}

/**
 * A fresh pair of lazy chooser components — the MapKit-first `Impl` and the
 * Google-or-unavailable `FallbackImpl`.
 *
 * It is a *factory*, not two module constants, because a resolved `React.lazy`
 * caches its result on the lazy object for the life of the module: once the
 * chooser has landed on "unavailable", re-rendering the same lazy never re-runs
 * the factory, so clearing the loaders' memoised `decision` alone would not
 * bring the map back. Retry calls this to mint brand-new lazies whose factories
 * run again against the freshly-reset loaders — that, plus a remount, is what
 * actually re-probes MapKit → Google → unavailable.
 */
/**
 * Why the chooser landed where it did, reported to the caller so the Map can
 * tell a self-healing blip from a settled outage:
 *   available    — an engine mounted; nothing to retry.
 *   transient    — both engines are unavailable, but at least one failed on a
 *                  401/timeout/5xx/network blip (see the loaders' lastTransient),
 *                  so an auto-retry is likely to recover.
 *   unconfigured — both engines are definitively off (all 501s), so retrying is
 *                  pointless and the unavailable card should show at once.
 */
type MapOutcome = 'available' | 'transient' | 'unconfigured';

type OutcomeRef = { current?: (o: MapOutcome) => void };

function makeEngines(onOutcome?: OutcomeRef) {
  const report = (o: MapOutcome) => onOutcome?.current?.(o);
  const Impl = lazy(async () => {
    // A thrown loader/chunk import is itself a transient condition — a degraded
    // connection or a mid-deploy chunk 404 — even when the MapKit probe had
    // already reported `available`. Capture it so the both-down classification
    // below counts it as `transient` (auto-retry) rather than mislabelling a
    // recoverable blip as a settled `unconfigured` outcome.
    let caught = false;
    try {
      const { mapkitReady } = await import('./mapkit-loader.ts');
      if (await mapkitReady()) {
        report('available');
        const m = await import('./map-mapkit.tsx');
        return { default: m.Map };
      }
    } catch (err) {
      caught = true;
      reportChunkError(err);
    }
    const fallback = await loadFallbackEngine();
    if (fallback.default === UnavailableImpl) {
      // Both engines are down. Ask the loaders WHY: a transient blip on either
      // is worth auto-retrying; both-definitively-off (501s) is the card, now.
      const [{ mapkitLastTransient }, { googleLastTransient }] = await Promise.all([
        import('./mapkit-loader.ts'),
        import('./google-loader.ts'),
      ]);
      report(
        caught || mapkitLastTransient() || googleLastTransient() ? 'transient' : 'unconfigured',
      );
    } else {
      report('available');
    }
    return fallback;
  });
  const FallbackImpl = lazy(loadFallbackEngine);
  return { Impl, FallbackImpl };
}

/**
 * The chooser handles failures BEFORE mount; this boundary handles failures
 * AFTER — a runtime crash inside the chosen engine. Without it, one bad line
 * in an engine unmounted the entire dashboard (staging, 2026-09-01: a
 * mistyped MapKit constant flashed the page and blanked it). An engine error
 * falls back to the next engine for the rest of the session; it never takes the
 * product with it.
 */
class MapEngineBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override componentDidCatch(error: unknown): void {
    console.warn('map engine crashed; falling back to the next engine', error);
    reportChunkError(error);
  }
  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export function Map({
  points,
  circles,
  polygons,
  height = 320,
  dark,
  onClick,
  onSelect,
  onDragEnd,
  onDrag,
  fallbackCenter,
  focus,
  insetLeft,
  projectorRef,
  satellite,
  onRegionChange,
}: {
  points: MapPoint[];
  /**
   * Radii to draw around points (building boundary, assembly areas — migration
   * 0111). Passed through to whichever engine renders; the unavailable panel
   * ignores them, because a circle with no map under it says nothing.
   */
  circles?: MapCircle[];
  /** Drawn areas (migration 0123) — rendered by both engines, same palette as circles. */
  polygons?: MapPolygon[];
  /** A number is pixels. A string is any CSS length, so the map can fill a layer. */
  height?: number | string;
  /** Omit to follow the interface. The live view sets it, because it is dark regardless. */
  dark?: boolean;
  onClick?: (latitude: number, longitude: number) => void;
  /**
   * A marker was tapped — the point's `id`. Wired identically in both engines
   * (MapKit annotation `select`, Google DomMarker `click`) so a tap behaves the
   * same whichever renders. Home uses it to open the building popover; a caller
   * that omits it gets plain, non-interactive markers.
   */
  onSelect?: (pointId: string) => void;
  /**
   * A draggable marker was dropped somewhere new — the point's id and where
   * it landed. Both engines fire it through the shared press-and-drag
   * (`lib/marker-drag.ts`), never the engine's own drag.
   */
  onDragEnd?: (pointId: string, latitude: number, longitude: number) => void;
  /**
   * A draggable marker is MOVING — fired continuously during the drag with its
   * current position, so a caller can draw live (the wizard's circle growing
   * under the resize handle). Both engines, like `onDragEnd`; the drop itself
   * still arrives through `onDragEnd`.
   */
  onDrag?: (pointId: string, latitude: number, longitude: number) => void;
  /**
   * Where to centre the base map when there are no points to frame — the org's
   * first placed site, its locality, or a sensible default. Its presence is
   * also what turns "no points" from the dashed "no location set" placeholder
   * into an actual base map: a screen that wants the map as its canvas (Home)
   * passes it; a per-field map that genuinely has nothing to show yet (a single
   * site with no coordinates on Places) omits it and keeps the honest empty
   * box.
   */
  fallbackCenter?: { latitude: number; longitude: number };
  /**
   * Centre and zoom the camera on one building **without** changing which pins
   * are drawn — focusing is a camera concern, never a filter (the safety rule:
   * every alerting pin stays on the map regardless of focus). Set it to a
   * building's coordinate to centre on it; `null`/omitted re-frames the whole
   * estate. Home drives it from the persisted estate/building view.
   */
  focus?: { latitude: number; longitude: number; span?: number; nonce?: number } | null;
  /**
   * Pixels of the map's LEFT edge covered by an overlaying panel (the building
   * wizard). Camera operations centre in the remaining visible area instead of
   * the viewport middle — otherwise a focused pin lands exactly under the
   * panel's edge. MapKit applies real map padding; the Google fallback
   * approximates with a longitude offset on focus.
   */
  insetLeft?: number;
  /**
   * Filled with the mounted engine's coordinate → viewport-pixel projector
   * ({@link MapProjector}); stays `null` on the unavailable panel, which has no
   * map to project onto.
   */
  /**
   * Draw the satellite imagery (labels kept) instead of the street map — the
   * building wizard turns it on once a building is placed, so the admin sees
   * the actual roof they are setting up (Trav 2026-09-29). Off everywhere else.
   */
  satellite?: boolean;
  projectorRef?: MapProjectorRef;
  /**
   * The visible box once the camera settles after a pan or zoom — both engines
   * report it; the unavailable panel has no camera and never calls it.
   */
  onRegionChange?: (region: MapRegion) => void;
}) {
  const resolved = useResolvedDark();
  const useDark = dark ?? resolved;

  // The chooser reports its outcome here. It is a ref (not a prop) so the async
  // lazy factory — minted once, earlier — always calls the LATEST handler; the
  // ref object itself is stable, so makeEngines can close over it.
  const onOutcomeRef = useRef<((o: MapOutcome) => void) | undefined>(undefined);

  // The lazy chooser lives in state so retry can replace it with a fresh pair
  // (a resolved React.lazy caches forever — see makeEngines), and `retryKey`
  // remounts the Suspense subtree so the engine boundaries drop any latched
  // `failed` state and re-run the whole MapKit → Google → unavailable chooser.
  const [engines, setEngines] = useState(() => makeEngines(onOutcomeRef));
  const [retryKey, setRetryKey] = useState(0);
  const { Impl, FallbackImpl } = engines;

  // Auto-retry budget for a TRANSIENT unavailable outcome (a 401/timeout/blip),
  // so a page-open hiccup self-heals instead of parking on the card until the
  // user clicks "Try again". A definitive `unconfigured` (all 501s) never
  // auto-retries. Bounded hard against a loop; the pending timer is cleared on
  // unmount and reset by a successful mount or a manual retry.
  const MAX_AUTO_RETRIES = 3;
  const autoRetries = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reprobe = useCallback(() => {
    // Clear both loaders' memoised readiness FIRST, then hand the subtree new
    // lazies and remount. The reset is awaited before `setEngines` so the new
    // chooser's factory cannot race ahead and read the stale decision — the
    // loader modules are already imported, so this resolves in a microtask and
    // the retry feels instant.
    void (async () => {
      const [mapkit, google] = await Promise.all([
        import('./mapkit-loader.ts'),
        import('./google-loader.ts'),
      ]);
      mapkit.resetMapkitDecision();
      google.resetGoogleDecision();
      setEngines(makeEngines(onOutcomeRef));
      setRetryKey((k) => k + 1);
    })();
  }, []);

  onOutcomeRef.current = (o: MapOutcome) => {
    if (o === 'available') {
      // A working mount clears the budget so a LATER blip gets its full retries.
      autoRetries.current = 0;
      if (timer.current) clearTimeout(timer.current);
      return;
    }
    if (o === 'transient' && autoRetries.current < MAX_AUTO_RETRIES) {
      autoRetries.current += 1;
      const delay = 1500 * autoRetries.current; // 1.5s, 3s, 4.5s — no thrash.
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(reprobe, delay);
    }
    // `unconfigured`, or the transient budget is spent — let the card stand.
  };

  // The unavailable panel's "Try again" — a deliberate user action, so it
  // refills the auto-retry budget and probes immediately.
  const retry = useCallback(() => {
    autoRetries.current = 0;
    if (timer.current) clearTimeout(timer.current);
    reprobe();
  }, [reprobe]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <MapRetryProvider onRetry={retry}>
      <Suspense
        key={retryKey}
        fallback={
          <div
            className="flex items-center justify-center rounded-[var(--radius-1)] border border-[var(--line)] text-[13px] text-[var(--ink-2)]"
            style={{ height }}
          >
            Loading the map…
          </div>
        }
      >
        <MapEngineBoundary
          fallback={
            // MapKit (or whichever engine was chosen) crashed after mount. Try
            // the fallback engine — and if THAT crashes too, the nested boundary
            // lands on the unavailable panel, which cannot itself throw. So a
            // post-mount crash degrades exactly like a pre-mount one, and never
            // blanks the dashboard.
            <MapEngineBoundary fallback={<UnavailableImpl points={points} height={height} />}>
              <FallbackImpl
                points={points}
                circles={circles}
                polygons={polygons}
                height={height}
                dark={useDark}
                onClick={onClick}
                onSelect={onSelect}
                onDragEnd={onDragEnd}
                onDrag={onDrag}
                fallbackCenter={fallbackCenter}
                focus={focus}
                insetLeft={insetLeft}
                projectorRef={projectorRef}
                satellite={satellite}
                onRegionChange={onRegionChange}
              />
            </MapEngineBoundary>
          }
        >
          <Impl
            points={points}
            circles={circles}
            polygons={polygons}
            height={height}
            dark={useDark}
            onClick={onClick}
            onSelect={onSelect}
            onDragEnd={onDragEnd}
            onDrag={onDrag}
            fallbackCenter={fallbackCenter}
            focus={focus}
            insetLeft={insetLeft}
            projectorRef={projectorRef}
            satellite={satellite}
            onRegionChange={onRegionChange}
          />
        </MapEngineBoundary>
      </Suspense>
    </MapRetryProvider>
  );
}

/** The legend, because a glyph nobody can decode is a decoration. */
export function MapKey({ kinds }: { kinds: MapPoint['kind'][] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
      {kinds.map((k) => (
        <span key={k} className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ink-2)]">
          <span
            aria-hidden
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] text-white"
            style={{ background: STYLE[k].colour }}
          >
            {STYLE[k].glyph}
          </span>
          {KIND_NAME[k]}
        </span>
      ))}
    </div>
  );
}
